import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search } from "lucide-react";

interface FileItem {
  id: string;
  uuid: string;
  filename: string;
  contentType: string;
  size: number;
  status: string;
  createdAt: string;
  user: { id: string; email: string };
  canvas: { uuid: string; title: string };
}

interface FileStats {
  totalFiles: number;
  totalSize: number;
  byStatus: Record<string, number>;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const STATUS_COLORS: Record<string, string> = { ACTIVE: "text-green-600", PROCESSING: "text-yellow-600", FAILED: "text-red-600", DELETED: "text-gray-400" };

function FilesPage() {
    const [files, setFiles] = React.useState<FileItem[]>([]);
  const [stats, setStats] = React.useState<FileStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const fetchFiles = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      params.set("sortBy", "createdAt");
      if (search) params.set("search", search);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/files?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setFiles(json.data.files);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, page]);

  React.useEffect(() => { fetchFiles(); }, [fetchFiles]);
  React.useEffect(() => {
    fetch(`${API_ADMIN_BASE_URL}/api/admin/files/stats`, { credentials: "include" })
      .then((r) => r.json()).then((j) => setStats(j.data)).catch(() => {});
  }, []);
  React.useEffect(() => { document.title = "Files - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Files</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Files & Storage</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} files{stats ? ` · ${formatBytes(stats.totalSize)} total` : ""}</p>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total Files</span><span className="text-lg font-semibold">{stats.totalFiles.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total Size</span><span className="text-lg font-semibold">{formatBytes(stats.totalSize)}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Active</span><span className="text-lg font-semibold">{(stats.byStatus.ACTIVE || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Deleted</span><span className="text-lg font-semibold">{(stats.byStatus.DELETED || 0).toLocaleString()}</span></div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="relative w-full md:w-[300px]">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input placeholder="Search filename or email..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Canvas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : files.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No files found</TableCell></TableRow>
                ) : files.map((f) => (
                  <TableRow key={f.uuid} className="hover:bg-muted/50">
                    <TableCell className="font-medium truncate max-w-[200px]">{f.filename}</TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{f.contentType}</span></TableCell>
                    <TableCell className="text-right text-sm">{formatBytes(f.size)}</TableCell>
                    <TableCell><span className="text-sm">{f.user.email}</span></TableCell>
                    <TableCell><span className="text-sm truncate max-w-[120px]">{f.canvas.title}</span></TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${STATUS_COLORS[f.status] || ""}`}>{f.status}</Badge></TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{fmtDate(f.createdAt)}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number; if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/files')({
  component: FilesPage,
})
